import { useSyncExternalStore } from 'react'

/**
 * Persona geçişinin sürüp sürmediği (089) — geçiş başladığı an içerik alanının ekrandan
 * kaldırılması için (`AppLayout` bu sürede içerik yerine bir yükleniyor göstergesi çizer).
 *
 * <p><b>Neden gerekli (kullanıcı bildirimi, 2026-10-13):</b> Görevler ekranındayken persona
 * değiştirilince eski kimliğin listesi 2-3 saniye ekranda kalıyordu. İçerik ağacı ancak geçişin
 * sonundaki yönlendirmede sökülüyor (`AppLayout`'taki persona anahtarı), oraya ise sunucu turları
 * bittikten sonra varılıyor; o süre boyunca ekran, yeni personanın göremeyeceği veriyi gösteriyordu.
 *
 * <p><b>Neden iki aşama ({@code pending} → {@code navigating}):</b> bayrak yönlendirmenin hemen
 * ardından kapatılamaz. React Router 7 konum güncellemesini `startTransition` içinde yapar
 * (`BrowserRouter`), bu deponun güncellemesi ise eşzamanlıdır — önce o işlenir ve içerik bir an ESKİ
 * rotada, YENİ persona başlığıyla kurulurdu: yetkisi olmayan uçlar 403 → "yetkiniz yok" toast'ı
 * (`useProxySwitch`'in 2026-08-26'da kapattığı hata). Bu yüzden kapanış, yönlendirmenin gerçekten
 * commit edildiğini gören kabuğa bırakılır ({@link completePersonaSwitchNavigation}).
 * {@code pending}'deyken konum değişse de (ör. geçiş sürerken sol menüden bir tık) bayrak kapanmaz.
 */
type Phase = 'idle' | 'pending' | 'navigating'

let phase: Phase = 'idle'
/**
 * Son başlayan geçişin numarası. Geçişler üst üste binebilir: vekaleti sunucuda iptal edilmiş bir
 * personadayken yapılan seçim `PROXY_NOT_ACTIVE` alır, aynı hata hem seçimin kendi hata dalına hem
 * kabuktaki kurtarmaya düşer. Sahiplik olmasaydı başarısız seçimin iptali, sürmekte olan kurtarmanın
 * bayrağını kapatır ve içerik eski rotada yeni kimlikle kurulurdu (bağımsız code review bulgusu,
 * 2026-10-13). Bu yüzden durumu yalnız EN SON başlayan geçiş ilerletebilir.
 */
let generation = 0
const listeners = new Set<() => void>()

function setPhase(next: Phase): void {
  if (phase === next) return
  phase = next
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function isSwitching(): boolean {
  return phase !== 'idle'
}

/**
 * Geçiş başladı — sunucuya gitmeden ÖNCE çağrılır ki eski veri ilk tıklamada kalksın.
 *
 * @returns Bu geçişin numarası; aşağıdaki iki çağrıya verilir.
 */
export function beginPersonaSwitch(): number {
  generation += 1
  setPhase('pending')
  return generation
}

/** Geçiş yönlendirmeye vardı; bayrağı kabuk, yeni konum commit edildiğinde kapatır. */
export function markPersonaSwitchNavigating(switchId: number): void {
  if (switchId === generation) setPhase('navigating')
}

/** Geçiş başarısız — persona değişmedi, içerik aynı kimlikle yeniden kurulur. */
export function cancelPersonaSwitch(switchId: number): void {
  if (switchId === generation) setPhase('idle')
}

/** Kabuk, konum her değiştiğinde çağırır; yalnız geçişin yönlendirmesi bekleniyorsa kapatır. */
export function completePersonaSwitchNavigation(): void {
  if (phase === 'navigating') setPhase('idle')
}

export function usePersonaSwitching(): boolean {
  return useSyncExternalStore(subscribe, isSwitching)
}

/** Test yardımcısı: modül durumunu sıfırla. */
export function __resetPersonaSwitch(): void {
  setPhase('idle')
}
